import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../api/client";
import { useAuth } from "../context/AuthContext";
import Card from "../components/Card";

interface TeamEvaluation {
  id: string;
  teamId: string;
  periodId: string;
  evaluatorId: string;
  status: "DRAFT" | "SUBMITTED" | "COMPLETED";
  scores: string;
  overallScore?: number;
  comments?: string;
  individualAvgScore?: number;
  submittedAt?: number;
  completedAt?: number;
  createdAt: number;
  updatedAt: number;
}

interface Team {
  id: string;
  name: string;
  description?: string;
}

function TeamEvaluationList() {
  const { user } = useAuth();
  const [evaluations, setEvaluations] = useState<TeamEvaluation[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<{
    status?: string;
    teamId?: string;
  }>({});

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.status) params.append("status", filter.status);
      if (filter.teamId) params.append("teamId", filter.teamId);

      const query = params.toString();
      const [evaluationsData, teamsData] = await Promise.all([
        apiClient.get<TeamEvaluation[] | { evaluations: TeamEvaluation[] }>(
          `/team-evaluations${query ? "?" + query : ""}`
        ),
        apiClient.get<{ teams: Team[] }>("/teams"),
      ]);

      const evalList = Array.isArray(evaluationsData)
        ? evaluationsData
        : evaluationsData.evaluations || [];

      setEvaluations(evalList);
      setTeams(teamsData.teams || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load team evaluations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  // Get team name
  const getTeamName = (teamId: string) => {
    return teams.find((t) => t.id === teamId)?.name || teamId;
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "DRAFT":
        return "bg-yellow-100 text-yellow-700";
      case "SUBMITTED":
        return "bg-blue-100 text-blue-700";
      case "COMPLETED":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading team evaluations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Team Evaluations</h2>
        <p className="text-sm text-slate-500 mt-1">View and manage team performance evaluations</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <Card title="Filters">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Team</label>
            <select
              value={filter.teamId || ""}
              onChange={(e) => setFilter({ ...filter, teamId: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            >
              <option value="">All Teams</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              value={filter.status || ""}
              onChange={(e) => setFilter({ ...filter, status: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilter({})}
              className="w-full px-3 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      </Card>

      {/* Evaluations List */}
      <div className="space-y-4">
        {evaluations.length === 0 ? (
          <Card>
            <div className="text-center py-8 text-slate-500">
              No team evaluations found. Create one to get started.
            </div>
          </Card>
        ) : (
          evaluations.map((evaluation) => (
            <Card
              key={evaluation.id}
              title={getTeamName(evaluation.teamId)}
              subtitle={`Period: ${evaluation.periodId}`}
              action={
                <Link
                  to={`/team-evaluations/${evaluation.id}/report`}
                  className="text-sm px-3 py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors"
                >
                  View Report
                </Link>
              }
            >
              <div className="space-y-4">
                {/* Status Row */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <span
                      className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${getStatusColor(
                        evaluation.status
                      )}`}
                    >
                      {evaluation.status}
                    </span>
                  </div>

                  <div className="text-right">
                    {evaluation.overallScore !== undefined && evaluation.overallScore !== null && (
                      <div>
                        <p className="text-sm text-slate-500">Team Score</p>
                        <p className="text-2xl font-bold text-indigo-600">
                          {evaluation.overallScore.toFixed(1)} / 5
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Comparison with Individual Average */}
                {evaluation.individualAvgScore !== undefined &&
                  evaluation.individualAvgScore !== null && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Individual Avg Score</p>
                          <p className="text-lg font-semibold text-slate-700">
                            {evaluation.individualAvgScore.toFixed(1)} / 5
                          </p>
                        </div>
                        {evaluation.overallScore && (
                          <div>
                            <p className="text-sm text-slate-500">Difference</p>
                            <p
                              className={`text-lg font-semibold ${
                                evaluation.overallScore >= evaluation.individualAvgScore
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {evaluation.overallScore >= evaluation.individualAvgScore ? "+" : ""}
                              {(evaluation.overallScore - evaluation.individualAvgScore).toFixed(1)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Details */}
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-slate-500">Evaluator ID</p>
                    <p className="font-mono text-slate-700">{evaluation.evaluatorId}</p>
                  </div>
                  {evaluation.comments && (
                    <div>
                      <p className="text-slate-500">Comments</p>
                      <p className="text-slate-700 mt-1">{evaluation.comments}</p>
                    </div>
                  )}
                </div>

                {/* Dates */}
                <div className="grid md:grid-cols-2 gap-4 text-xs text-slate-500 pt-4 border-t border-slate-200">
                  <div>
                    <p>Created: {new Date(evaluation.createdAt).toLocaleDateString()}</p>
                  </div>
                  {evaluation.submittedAt && (
                    <div>
                      <p>Submitted: {new Date(evaluation.submittedAt).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

export default TeamEvaluationList;
